import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiRefreshCw } from "react-icons/fi";

import PageHeader from "../components/ui/PageHeader";
import StatCard from "../components/ui/StatCard";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import ProgressBar from "../components/ui/ProgressBar";
import BarChart from "../components/charts/BarChart";
import DonutChart from "../components/charts/DonutChart";
import ProcessFlow from "../components/charts/ProcessFlow";
import useAuth from "../hooks/useAuth";
import { useSeason } from "../context/SeasonContext";

import "./OrganiserDashboard.css";

const API = "https://maize.norissolutions.in/Php/public/api";

export default function OrganiserDashboard() {
  const token = sessionStorage.getItem("maaze_token");
  const { refreshToken } = useAuth();

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const [loading, setLoading] = useState(true);

  const [lifecycle, setLifecycle] = useState(null);
  const [cards, setCards] = useState({});
  const [monthly, setMonthly] = useState([]);
  const [stock, setStock] = useState([]);
  const [organisers, setOrganisers] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);

      const [
        lifecycleRes,
        cardsRes,
        monthlyRes,
        stockRes,
        organiserRes,
        activityRes,
      ] = await Promise.all([
        axios.get(`${API}/production-lifecycle`, {
          headers,
        }),

        axios.get(`${API}/dashboard-cards`, {
          headers,
        }),

        axios.get(`${API}/dashboard/monthly-distribution`, {
          headers,
        }),

        axios.get(`${API}/dashboard/stock-distribution`, {
          headers,
        }),

        axios.get(
          `${API}/dashboard/organiser-performance`,
          { headers },
        ),

        axios.get(`${API}/dashboard/recent-activity`, {
          headers,
        }),
      ]);

      /* ================= Lifecycle ================= */

      setLifecycle(lifecycleRes.data?.data || null);

      /* ================= Dashboard Cards ================= */

      setCards(cardsRes.data?.data || {});

      /* ================= Monthly Distribution ================= */

      const monthlyData = (monthlyRes.data?.data || []).map((m) => ({
        label: m.month ?? "",
        val: m.quantity_kg ?? 0,
      }));

      setMonthly(monthlyData);

      /* ================= Stock Distribution ================= */

      const s = stockRes.data?.data || {};

      setStock([
        { label: "Distributed", value: s.distributed ?? 0, color: "#22c55e" },
        { label: "Warehouse", value: s.warehouse ?? 0, color: "#3b82f6" },
        { label: "Processing", value: s.processing ?? 0, color: "#f59e0b" },
        { label: "Returned", value: s.returned ?? 0, color: "#ef4444" },
      ]);

      /* ================= Organiser Performance ================= */

      const organiserData =
        organiserRes.data?.data?.map((o) => ({
          name: o.organiser,
          villages: o.villages,
          farmers: o.farmers,
          collected: o.collected_kg,
          target: o.target_kg,
          pct: o.target_percent,
        })) || [];

      setOrganisers(organiserData);

      /* ================= Activity ================= */

      setActivity(activityRes.data?.data || []);
    } catch (error) {
      console.error("Dashboard API Error:", error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const { seasons, selectedSeason } = useSeason() || {};
  const seasonName = seasons?.find(s => s.season_id.toString() === selectedSeason?.toString())?.season_name || "Active Season";

  if (!token) {
    return <div style={{ padding: 40 }}>Authentication required</div>;
  }

  return (
    <div>
      <PageHeader
        title="Operations Overview"
        subtitle={
          <>
            Season {seasonName} · <span className="live-dot" />
            Live tracking active
          </>
        }
      >
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      {/* ================= Production Lifecycle ================= */}

      <Card
        title="Production Lifecycle"
        action={<Badge color="green">Active Season</Badge>}
        style={{ marginBottom: 24 }}
      >
        {lifecycle && (
          <ProcessFlow
            activeUpTo={
              Object.keys(lifecycle.stages || {}).findIndex(
                (s) => s === lifecycle.current_stage,
              ) + 1
            }
          />
        )}
      </Card>

      {/* ================= KPI Cards ================= */}

      <div className="stats-grid">
        <StatCard
          label="Total Investment"
          value={cards.total_investment || 0}
          unit="₹"
          icon="💵"
        />

        <StatCard
          label="Raw Seeds"
          value={cards.raw_seeds || 0}
          unit="kg"
          icon="🌾"
          variant="gold"
        />

        <StatCard
          label="Active Farmers"
          value={cards.active_farmers || 0}
          icon="👨‍🌾"
          variant="sky"
        />

        <StatCard
          label="Collected Crop"
          value={cards.collected_crop || 0}
          unit="kg"
          icon="📦"
        />

        <StatCard
          label="Dried Stock"
          value={cards.dried_stock || 0}
          unit="kg"
          icon="🏭"
          variant="gold"
        />

        <StatCard
          label="Pending Approval"
          value={cards.pending_qc || 0}
          icon="⚠️"
          variant="red"
        />
      </div>

      {/* ================= Charts ================= */}

      <div className="grid-2 mb-24">
        <Card
          title="Monthly Distribution"
          subtitle="Seeds distributed to farmers (kg)"
        >
          <BarChart data={monthly} />
        </Card>

        <Card
          title="Stock Distribution"
          subtitle="Current inventory allocation"
        >
          <DonutChart segments={stock} />
        </Card>
      </div>

      {/* ================= Bottom Row ================= */}

      <div className="grid-2">
        {/* Organiser Performance */}

        <Card title="Organiser Performance" noPad>
          <table className="perf-table">
            <thead>
              <tr>
                <th>Organiser</th>
                <th>Villages</th>
                <th>Farmers</th>
                <th>Collected</th>
                <th>Target</th>
              </tr>
            </thead>

            <tbody>
              {organisers.map((o) => (
                <tr key={o.name}>
                  <td>{o.name}</td>
                  <td>{o.villages}</td>
                  <td>{o.farmers}</td>
                  <td>{o.collected}</td>

                  <td>
                    <div className="perf-table__progress-row">
                      <ProgressBar value={o.pct} />
                      <span>{o.pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Recent Activity */}

        <Card title="Recent Activity">
          <div className="activity-list">
            {activity.map((a, i) => (
              <div className="activity-item" key={i}>
                <div className="activity-item__body">
                  <div className="activity-item__title">{a.activity}</div>
                  <div className="activity-item__time">{a.activity_time}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
